// @spec NIS2-STR-038
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { AuditorNIS2GapDetail } from '@/features/auditor-access/components/AuditorNIS2GapDetail';
import { AuditorNIS2Gaps } from '@/features/auditor-access/components/AuditorNIS2Gaps';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/nis2/gaps')({
  component: AuditorNIS2GapsPage,
});

function AuditorNIS2GapsPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const [selectedGapId, setSelectedGapId] = useState<string | null>(null);

  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!currentAccess.accessScope.includes('nis2_compliance')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              Your auditor access does not include permission to view NIS2 compliance data.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const handleGapClick = (gapId: string) => {
    setSelectedGapId(gapId);
  };

  const handleBackToList = () => {
    setSelectedGapId(null);
  };

  const handleBackToOverview = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2` });
  };

  return (
    <div className="min-h-screen bg-background">
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          {selectedGapId ? (
            <AuditorNIS2GapDetail
              accessId={accessId}
              gapId={selectedGapId}
              onBack={handleBackToList}
            />
          ) : (
            <AuditorNIS2Gaps
              accessId={accessId}
              organizationName={currentAccess.name}
              onGapClick={handleGapClick}
              onBackToOverview={handleBackToOverview}
            />
          )}
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
